import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db, storage } from "../../firebase";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

/**
 * UserProfileCard
 * ------------------------------------------------------------
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - personRef: { id?: string, email?: string, name?: string, type?: "leader"|"recruiter"|"prospect" }
 *    ^ used to locate the right person across collections
 */
export default function UserProfileCard({ open, onClose, personRef = {}, person = null }) {
  const [loading, setLoading] = useState(true);

  // Core sources
  const [userProfile, setUserProfile] = useState(null); // userprofile
  const [userProfileDocId, setUserProfileDocId] = useState(null);
  const [userAccount, setUserAccount] = useState(null); // users
  const [prospect, setProspect] = useState(null); // prospects
  const [associate, setAssociate] = useState(null); // associate_tracker

  // Trackers
  const [fourByFour, setFourByFour] = useState(null); // 4x4_tracker (wheels)
  const [license, setLicense] = useState(null); // license_tracker (steps)

  // Inline edits
  const [poloSizeEdit, setPoloSizeEdit] = useState("");
  const [spousePoloSizeEdit, setSpousePoloSizeEdit] = useState("");

  // Upload
  const fileInputRef = useRef(null);

  // ------------------- Lookup helpers -------------------
  // Support both legacy prop `personRef` and newer `person` alias
  const effectivePersonRef = useMemo(() => {
    const hasPersonRef = personRef && Object.keys(personRef || {}).length > 0;
    return hasPersonRef ? personRef : person || {};
  }, [personRef, person]);

  const matchers = useMemo(() => {
    const arr = [];
    if (effectivePersonRef?.id) arr.push(["userId", "==", effectivePersonRef.id]);
    if (effectivePersonRef?.email) arr.push(["email", "==", effectivePersonRef.email]);
    if (effectivePersonRef?.name) arr.push(["name", "==", effectivePersonRef.name]);
    return arr;
  }, [effectivePersonRef]);

  async function findOne(coll) {
    // Try exact matches first
    for (const [field, op, val] of matchers) {
      const snap = await getDocs(
        query(collection(db, coll), where(field, op, val), limit(1))
      );
      if (!snap.empty) {
        const d = snap.docs[0];
        return { id: d.id, data: d.data() };
      }
    }
    // Fallback: fuzzy match on lowercase name/email
    const scan = await getDocs(query(collection(db, coll), limit(200)));
    const by = (x) => (x || "").toString().trim().toLowerCase();
    const targetName = by(personRef?.name);
    const targetEmail = by(personRef?.email);
    let hit = null;
    scan.forEach((d) => {
      if (hit) return;
      const v = d.data();
      const vName = by(v.name || v.displayName);
      const vEmail = by(v.email);
      if (
        (targetEmail && vEmail === targetEmail) ||
        (targetName && vName === targetName)
      ) {
        hit = { id: d.id, data: v };
      }
    });
    return hit;
  }

  const adapt4x4 = (docData) => {
    if (!docData) return null;
    if (docData.wheels) return docData.wheels;
    const def = (label, key, type = "check") => ({ label, key, type });
    const pack = (obj, defs) =>
      defs.map((d) => ({
        label: d.label,
        type: d.type,
        value:
          obj?.[d.key] ??
          obj?.[d.key + "Date"] ??
          obj?.[d.key + "Value"] ??
          null,
      }));

    return {
      wheel1: pack(docData, [
        def("Saving", "saving", "money"),
        def("Finish Lic class", "finishLicClass"),
        def("ABC/Orientation", "abcOrientation", "select"),
      ]),
      wheel2: pack(docData, [
        def("Set 3 App", "set3App"),
        def("Finish 1st BP", "finish1stBp"),
        def("Savings", "w2Savings"),
        def("Practice Exam", "practiceExam"),
        def("ABC/Orientation", "w2AbcOrientation", "select"),
      ]),
      wheel3: pack(docData, [
        def("Finish 2nd BP", "finish2ndBp"),
        def("Savings", "w3Savings"),
        def("Pass Exam", "passExam", "date"),
        def("National Call", "nationalCall"),
      ]),
      wheel4: pack(docData, [
        def("Finish 3rd BP", "finish3rdBp"),
        def("Savings", "w4Savings"),
        def("Application", "application", "date"),
        def("Big Event Reg", "bigEventReg"),
        def("Net License", "netLicense"),
        def("Bronze Practice", "bronzePractice"),
      ]),
    };
  };

  const adaptLicense = (docData) => {
    if (!docData) return null;
    if (Array.isArray(docData.steps)) return docData.steps;
    const known = [
      ["Sign up on Pre Licensing (ExamFX)", "preLicensing"],
      ["Sign up for your test & Set up your test Date", "testSignup"],
      ["Pass the test", "passTest"],
      ["Get your fingerprinting done", "fingerprinting"],
      ["Fill out Sircon/NIPR application with state", "sirconApply"],
      [
        "Receive your Licenses Certificate from Sircon/NIPR on your email",
        "licenseCertEmail",
      ],
      [
        "Create your username, password and log in to WFGlaunch/WFGdirect",
        "createWfgLaunch",
      ],
      [
        "Log into WFGlaunch/WFGdirect and submit Transamerica app",
        "transamericaApp",
      ],
      ["Finish Continuing Education courses", "ceCourses"],
      [
        "Receive approval of your agent agreement from WFG on your email",
        "agentAgreement",
      ],
      ["Log into MyWFG & sign up for E&O platform", "mywfgEO"],
      ["Log into MyWFG & sign up for direct deposits", "mywfgDeposits"],
    ];
    return known.map(([label, key]) => ({
      label,
      approved: !!docData[key],
      date: docData[key + "Date"] || null,
    }));
  };

  // --------------- Load data across collections ---------------
  useEffect(() => {
    if (!open) return;
    let alive = true;
    const run = async () => {
      setLoading(true);
      try {
        // Core profile (userprofile)
        const up = await findOne("userprofile");
        if (alive) {
          setUserProfile(up?.data || null);
          setUserProfileDocId(up?.id || null);
          setPoloSizeEdit(up?.data?.poloSize || "");
          setSpousePoloSizeEdit(up?.data?.spousePoloSize || "");
        }

        // Prospect + flags + notes
        const pr = await findOne("prospects");
        if (alive) setProspect(pr?.data || null);

        // Associate (key player) now read from unified trackers
        const trackersDoc = await findOne("trackers");
        if (alive) setAssociate(trackersDoc?.data?.associate || trackersDoc?.data || null);

        // User account (level/plan/status)
        const ua = await findOne("users");
        if (alive) setUserAccount(ua?.data || null);

        // Trackers (4x4 + license) now read from unified trackers
        if (alive) {
          setFourByFour(adapt4x4(trackersDoc?.data));
          setLicense(adaptLicense(trackersDoc?.data));
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [open, effectivePersonRef]); // eslint-disable-line

  // ---------- Derived presentation ----------
  const base = useMemo(() => {
    const up = userProfile || {};
    const ua = userAccount || {};
    const pr = prospect || {};
    const p = pr.profile || {};
    const createdAt = up.createdAt || pr.createdAt || ua.createdAt;

    return {
      // header
      name: up.name || pr.name || p.name || effectivePersonRef?.name || "—",
      photoUrl: up.photoUrl || p.photoUrl || "",
      hot: !!pr.hot,

      // header actions
      calendarUrl: up.calendarUrl || p.calendarUrl || pr.calendarUrl || "",

      // left column (order fixed)
      amaDate: firstDate(createdAt) || up.amaDate || "",
      agencyCode: up.agencyCode || p.agencyCode || pr.agencyCode || "",
      email: up.email || p.email || pr.email || effectivePersonRef?.email || "",
      phone: up.phone || p.phone || pr?.profile?.phone || "",
      dob: up.birthday || p.birthday || pr?.profile?.birthday || up.dob || "",
      homeAddress:
        up.homeAddress ||
        [up.homeAddressLine, up.city, up.state, up.zip || up.postalCode]
          .filter(Boolean)
          .join(" ") ||
        [p.city, p.state].filter(Boolean).join(", "),
      profile: profileLineFromProspect(pr),
      keyPlayer: associate?.keyPlayer ?? associate?.isKeyPlayer ?? "",
      level: ua.level || up.level || "",
      poloSize: up.poloSize || "",
      spouseName:
        p.spouseName || up.spouseName || pr.spouseName || p?.spouse?.name || "",
      spousePhone:
        p.spousePhone ||
        up.spousePhone ||
        pr.spousePhone ||
        p?.spouse?.phone ||
        "",
      spousePoloSize: up.spousePoloSize || "",
      recruiter: pr.recruiter || "",
      leader: pr.leader || "",
      plan: ua.plan || up.plan || "",
      status: ua.status || up.status || "",
      notes: notesText(pr?.notes),
    };
  }, [userProfile, userAccount, prospect, associate, effectivePersonRef]);

  const ageText = useMemo(() => {
    if (!base.dob) return "—";
    const d = toDate(base.dob);
    if (!d) return "—";
    const age = calcAge(d);
    return `${formatDate(d)} • ${age} yrs`;
  }, [base.dob]);

  // --------------- Photo upload ---------------
  async function handleUploadPhoto(file) {
    if (!file || !userProfileDocId) return;
    try {
      const path = `profile_photos/${userProfileDocId}/${Date.now()}_${
        file.name
      }`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file);
      const url = await getDownloadURL(ref);
      await updateDoc(doc(db, "userprofile", userProfileDocId), {
        photoUrl: url,
      });
      setUserProfile((prev) => ({ ...(prev || {}), photoUrl: url }));
    } catch (e) {
      alert("Upload failed. Check Firebase Storage config.");
      console.error(e);
    }
  }

  // --------------- Save inline sizes ---------------
  async function saveSizes() {
    if (!userProfileDocId) return;
    try {
      await updateDoc(doc(db, "userprofile", userProfileDocId), {
        poloSize: poloSizeEdit || "",
        spousePoloSize: spousePoloSizeEdit || "",
      });
      setUserProfile((p) => ({
        ...(p || {}),
        poloSize: poloSizeEdit || "",
        spousePoloSize: spousePoloSizeEdit || "",
      }));
    } catch (e) {
      console.error(e);
    }
  }

  if (!open) return null;

  return (
    <div className="upc-backdrop" onClick={onClose}>
      <div
        className="upc-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal
      >
        {/* Header */}
        <header className="upc-head">
          <div className="upc-id">
            <div className="upc-photo-wrap">
              {base.photoUrl ? (
                <img
                  src={base.photoUrl}
                  alt={base.name || "Profile"}
                  className="upc-photo"
                />
              ) : (
                <div className="upc-photo fallback">
                  {(base.name || "?").slice(0, 1)}
                </div>
              )}

              {/* Buttons next to avatar */}
              <div className="upc-avatar-actions">
                <button
                  className="pill"
                  onClick={() => fileInputRef.current?.click()}
                  title="Change photo"
                >
                  Change Photo
                </button>
                {base.calendarUrl ? (
                  <a
                    className="pill"
                    href={base.calendarUrl}
                    target="_blank"
                    rel="noreferrer"
                    title="Open calendar"
                  >
                    Calendar
                  </a>
                ) : null}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUploadPhoto(f);
                  e.target.value = "";
                }}
              />
            </div>

            <div className="upc-titles">
              <div className="upc-name" title={base.name}>
                {base.name}
              </div>
              <div className="upc-sub">
                {effectivePersonRef?.type ? effectivePersonRef.type.toUpperCase() : "PROFILE"}
                {base.hot ? " • 🔥 HOT" : ""}
              </div>
            </div>
          </div>

          <button className="upc-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        {/* 3 columns */}
        <div className="upc-grid">
          {/* LEFT: Profile in requested order */}
          <section className="upc-section">
            <div className="upc-section-title">Profile</div>
            <div className="upc-fields">
              <Field label="AMA Date" value={formatDate(base.amaDate)} />
              <Field label="Agency Code" value={base.agencyCode} />

              <Field label="Name" value={base.name} truncate />
              <Field
                label="Email"
                value={base.email}
                actions={[
                  base.email && {
                    label: "✉",
                    title: "Email",
                    onClick: () => window.open(`mailto:${base.email}`),
                  },
                  base.email && {
                    label: "⧉",
                    title: "Copy email",
                    onClick: () =>
                      navigator.clipboard?.writeText(String(base.email)),
                  },
                ].filter(Boolean)}
              />

              <Field
                label="Phone"
                value={formatPhone(base.phone)}
                actions={[
                  base.phone && {
                    label: "📞",
                    title: "Call",
                    onClick: () => window.open(`tel:${digits(base.phone)}`),
                  },
                  base.phone && {
                    label: "💬",
                    title: "Text",
                    onClick: () => window.open(`sms:${digits(base.phone)}`),
                  },
                ].filter(Boolean)}
              />

              <Field label="DOB" value={ageText} />
              <Field
                label="Home Address"
                value={base.homeAddress}
                multiline
                actions={[
                  base.homeAddress && {
                    label: "⧉",
                    title: "Copy address",
                    onClick: () =>
                      navigator.clipboard?.writeText(String(base.homeAddress)),
                  },
                ].filter(Boolean)}
              />
              <Field label="Profile" value={base.profile || "—"} />
              <Field label="Key Player" value={yn(base.keyPlayer)} />
              <Field label="Level" value={base.level} />

              {/* Poloshirt Size — editable */}
              <Field
                label="Poloshirt Size"
                custom={
                  <InlineSizeEditor
                    value={poloSizeEdit}
                    setValue={setPoloSizeEdit}
                    onSave={saveSizes}
                  />
                }
              />

              <Field label="Spouse Name" value={base.spouseName} />
              <Field
                label="Spouse Phone"
                value={formatPhone(base.spousePhone)}
                actions={[
                  base.spousePhone && {
                    label: "📞",
                    title: "Call",
                    onClick: () =>
                      window.open(`tel:${digits(base.spousePhone)}`),
                  },
                ].filter(Boolean)}
              />

              {/* Spouse Poloshirt Size — editable */}
              <Field
                label="Spouse Poloshirt Size"
                custom={
                  <InlineSizeEditor
                    value={spousePoloSizeEdit}
                    setValue={setSpousePoloSizeEdit}
                    onSave={saveSizes}
                  />
                }
              />

              <Field label="Recruiter" value={base.recruiter} />
              <Field label="Leader" value={base.leader} />
              <Field label="Plan" value={base.plan} />
              <Field label="Status" value={base.status} />
              <Field label="Notes" value={base.notes || "—"} multiline />
            </div>
          </section>

          {/* MIDDLE: 4x4 Onboarding */}
          <section className="upc-section">
            <div className="upc-section-title">Onboarding</div>
            {loading ? (
              <div className="upc-empty">Loading…</div>
            ) : !fourByFour ? (
              <div className="upc-empty">No 4×4 tracker data.</div>
            ) : (
              <div className="wheels">
                {["wheel1", "wheel2", "wheel3", "wheel4"].map((k) => (
                  <Wheel key={k} title={wheelTitle(k)} rows={fourByFour[k]} />
                ))}
              </div>
            )}
          </section>

          {/* RIGHT: License Tracker */}
          <section className="upc-section">
            <div className="upc-section-title">License Tracker</div>
            {loading ? (
              <div className="upc-empty">Loading…</div>
            ) : !license ? (
              <div className="upc-empty">No license tracker data.</div>
            ) : (
              <ul className="lic-list">
                {license.map((s, i) => (
                  <li key={i} className="lic-row">
                    <span
                      className={`tick ${s.approved ? "ok" : ""}`}
                      aria-hidden
                    >
                      {s.approved ? "✔" : "○"}
                    </span>
                    <div className="lic-main">
                      <div className="lic-title">{s.label}</div>
                      <div className="lic-date">{formatDate(s.date)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <style>{styles}</style>
      </div>
    </div>
  );
}

/* =========================================================
   Presentational bits
   ========================================================= */
function Field({ label, value, actions = [], multiline, truncate, custom }) {
  return (
    <div className="upc-field">
      <div className="upc-field-label">{label}</div>
      <div
        className={`upc-field-value ${multiline ? "multiline" : ""} ${
          truncate ? "truncate" : ""
        }`}
        title={typeof value === "string" ? value : undefined}
      >
        {custom ? custom : value == null || value === "" ? "—" : value}
        {actions?.length ? (
          <div className="row-actions">
            {actions.map(
              (a, i) =>
                a && (
                  <button
                    key={i}
                    className="row-action-btn"
                    title={a.title}
                    onClick={a.onClick}
                    type="button"
                  >
                    {a.label}
                  </button>
                )
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function InlineSizeEditor({ value, setValue, onSave }) {
  const [dirty, setDirty] = useState(false);
  return (
    <div className="size-editor">
      <input
        className="size-input"
        value={value || ""}
        onChange={(e) => {
          setValue(e.target.value);
          setDirty(true);
        }}
        placeholder="e.g., M / L / XL"
      />
      <button
        className="row-action-btn"
        onClick={() => {
          onSave();
          setDirty(false);
        }}
        disabled={!dirty}
        title="Save"
      >
        💾
      </button>
    </div>
  );
}

function Wheel({ title, rows = [] }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="wheel">
      <div className="wheel-head">{title}</div>
      <div className="wheel-grid">
        {rows.map((r, i) => (
          <div key={i} className="wheel-row">
            <div className="wheel-label">{r.label}</div>
            <div className="wheel-val">
              {r.type === "check" ? (
                <span className={`tick ${truthy(r.value) ? "ok" : ""}`}>
                  {truthy(r.value) ? "✔" : "○"}
                </span>
              ) : r.type === "date" ? (
                <span className="date-chip">{formatDate(r.value)}</span>
              ) : r.type === "money" ? (
                <span className="money">{fmtMoney(r.value)}</span>
              ) : (
                <span>{r?.value ?? "—"}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* =========================================================
   Styles (glass + robust layout; no overlapping)
   ========================================================= */
const styles = `
.upc-backdrop{position:fixed;inset:0;background:rgba(10,12,16,.55);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;z-index:2200}
.upc-card{
  width:min(1140px,96vw);
  max-height:92vh; overflow:auto;
  padding:18px;
  border-radius:22px;
  border:1px solid rgba(255,255,255,.18);
  background:linear-gradient(180deg,rgba(255,255,255,.10),rgba(255,255,255,.06));
  backdrop-filter: blur(22px);
  box-shadow: 0 30px 80px rgba(0,0,0,.55), inset 0 1px 0 rgba(255,255,255,.06);
  color:#fff;
}
.upc-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;gap:12px}
.upc-id{display:flex;align-items:center;gap:14px;min-width:0}
.upc-photo-wrap{position:relative;display:flex;align-items:center;gap:14px}
.upc-photo,.upc-photo.fallback{
  width:104px;height:104px;border-radius:20px;
  background:#0f1115;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:36px;
  border:1px solid rgba(255,255,255,.18); box-shadow:0 8px 22px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.08);
  object-fit:cover;
}
.upc-avatar-actions{display:flex;gap:8px;flex-wrap:wrap}
.pill{
  display:inline-flex;align-items:center;justify-content:center;
  height:34px;padding:0 12px;border-radius:12px;
  background:rgba(255,255,255,.10);border:1px solid rgba(255,255,255,.25);color:#fff;cursor:pointer;text-decoration:none
}
.pill:hover{background:rgba(255,255,255,.16)}
.upc-titles{min-width:0}
.upc-name{font-weight:900;font-size:22px;letter-spacing:.2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:min(48vw,520px)}
.upc-sub{opacity:.9;font-size:13px;margin-top:2px}
.upc-close{border:none;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.22);color:#fff;width:36px;height:36px;border-radius:12px;font-size:18px;cursor:pointer;flex:0 0 auto}
.upc-close:hover{background:rgba(255,255,255,.14)}

.upc-grid{
  display:grid; gap:14px;
  grid-template-columns: minmax(300px, 1.1fr) minmax(320px,1.2fr) minmax(300px,1fr);
}
.upc-section{
  border-radius:18px; padding:12px;
  background:linear-gradient(180deg,rgba(255,255,255,.08),rgba(255,255,255,.04));
  border:1px solid rgba(255,255,255,.14);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.06);
  min-height: 120px;
}
.upc-section-title{font-weight:900;letter-spacing:.2px;margin:4px 2px 10px;font-size:14px;opacity:.95}

.upc-fields{display:grid;grid-template-columns:1fr;gap:8px}
.upc-field{display:grid;grid-template-columns:160px 1fr; gap:8px; align-items:center; min-height:36px}
.upc-field-label{opacity:.85;font-size:12px}
.upc-field-value{
  display:flex; align-items:center; gap:8px; min-height:36px;
  background:rgba(0,0,0,.18); border:1px solid rgba(255,255,255,.10);
  border-radius:10px; padding:6px 10px; line-height:1.25;
  overflow:hidden; text-overflow:ellipsis;
}
.upc-field-value.multiline{align-items:flex-start; white-space:pre-wrap}
.upc-field-value.truncate{white-space:nowrap}
.row-actions{margin-left:auto; display:flex; gap:6px}
.row-action-btn{
  background:rgba(255,255,255,.10); border:1px solid rgba(255,255,255,.18);
  color:#fff; border-radius:8px; padding:2px 8px; cursor:pointer
}
.size-editor{display:flex; align-items:center; gap:8px; width:100%}
.size-input{
  flex:1 1 auto; background:transparent; color:#fff; border:none; outline:none;
}

.upc-empty{opacity:.8;font-size:13px;padding:10px}

.wheels{display:grid;gap:10px}
.wheel{border-radius:14px;border:1px solid rgba(255,255,255,.12);background:rgba(0,0,0,.16)}
.wheel-head{font-weight:800;padding:8px 10px;border-bottom:1px solid rgba(255,255,255,.10);background:linear-gradient(180deg,rgba(255,255,255,.08),transparent)}
.wheel-grid{display:grid;grid-template-columns: 1fr auto;gap:0}
.wheel-row{display:contents}
.wheel-label{padding:8px 10px;border-bottom:1px dashed rgba(255,255,255,.08);opacity:.95}
.wheel-val{padding:8px 10px;border-bottom:1px dashed rgba(255,255,255,.08);text-align:right}
.tick{display:inline-flex;width:18px;height:18px;border-radius:999px;align-items:center;justify-content:center;border:1px solid rgba(255,255,255,.35);font-size:12px}
.tick.ok{background:#10b981;border-color:#10b981;color:#fff}
.date-chip{display:inline-flex;align-items:center;justify-content:center;padding:2px 8px;border-radius:999px;background:rgba(255,255,255,.10);border:1px solid rgba(255,255,255,.18);font-size:12px}
.money{font-weight:800}

.lic-list{list-style:none;margin:0;padding:0;display:grid;gap:8px}
.lic-row{display:flex;gap:10px;align-items:center;padding:6px;border-radius:12px;background:rgba(0,0,0,.16);border:1px solid rgba(255,255,255,.10)}
.lic-main{display:flex;flex:1;align-items:center;justify-content:space-between;gap:10px}
.lic-title{font-size:13px}
.lic-date{font-size:12px;opacity:.8}

@media (max-width: 980px){ .upc-grid{grid-template-columns:1fr} }
`;

/* =========================================================
   Utilities
   ========================================================= */
function toDate(v) {
  try {
    if (!v) return null;
    if (v?.toDate) return v.toDate();
    if (typeof v === "object" && v.seconds) return new Date(v.seconds * 1000);
    return new Date(v);
  } catch {
    return null;
  }
}
function firstDate(v) {
  const d = toDate(v);
  return d && isFinite(d.getTime()) ? d : null;
}
function formatDate(v) {
  const d = toDate(v);
  if (!d || !isFinite(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
}
function calcAge(date) {
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const m = today.getMonth() - date.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < date.getDate())) age--;
  return age;
}
function fmtMoney(v) {
  const n = Number(String(v ?? "").replace(/[^\d.-]/g, ""));
  if (!isFinite(n)) return "—";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}
function truthy(v) {
  return !!(
    v === true ||
    v === "true" ||
    v === 1 ||
    v === "1" ||
    v === "yes" ||
    v === "ok"
  );
}
function yn(v) {
  if (v === "" || v == null) return "—";
  return truthy(v) ? "Yes" : "No";
}
function digits(s) {
  return (s || "").replace(/[^\d+]/g, "");
}
function formatPhone(s) {
  const d = digits(s || "");
  if (!d) return "—";
  if (d.length === 10)
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return s;
}
function notesText(notes) {
  if (!notes) return "";
  if (typeof notes === "string") return notes;
  if (Array.isArray(notes)) {
    return notes
      .map((n) => (typeof n === "string" ? n : n?.text || ""))
      .filter(Boolean)
      .join("\n");
  }
  if (typeof notes === "object" && notes.text) return notes.text;
  return "";
}
function profileLineFromProspect(pr = {}) {
  const p = pr.profile || {};
  const flags = p.flags || {};
  const parts = [
    p.howKnown,
    p.relationship || p.relationship === 0 ? `${p.relationship}/10` : null,
    p.occupation,
    flags.married ? "Married" : null,
    flags.dependentChildren ? "Kids" : null,
    p.birthday ? `Age: ${calcAge(toDate(p.birthday) || new Date())}` : null,
  ].filter(Boolean);
  return parts.join(" • ");
}
function wheelTitle(k) {
  return (
    {
      wheel1: "Wheel 1",
      wheel2: "Wheel 2",
      wheel3: "Wheel 3",
      wheel4: "Wheel 4",
    }[k] || k
  );
}
